'use client';

import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { Pencil, ZoomIn, ZoomOut } from 'lucide-react';
import { setPublicAuthorProfileImageUrlAction } from '@/app/actions/public-author-auth-actions';
import { setUserProfileImageUrlAction } from '@/app/actions/auth-actions';
import Cropper from 'react-easy-crop';
import getCroppedImg from '@/lib/cropUtils';

type LibraryItem = { url: string; name: string };

export default function ProfileImageEditButton({
  kind,
  profileKind,
  buttonClassName,
  iconSize,
  currentCoverUrl,
  currentAvatarUrl,
}: {
  kind: 'cover' | 'avatar';
  profileKind: 'author' | 'user';
  buttonClassName: string;
  iconSize: number;
  currentCoverUrl?: string | null;
  currentAvatarUrl?: string | null;
}) {
  const router = useRouter();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryTab, setLibraryTab] = useState<'photos' | 'albums'>('photos');
  const [album, setAlbum] = useState<'cover' | 'profile' | null>(null);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  const [libraryItems, setLibraryItems] = useState<LibraryItem[]>([]);
  const [selectedUrl, setSelectedUrl] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Cropper States
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [originalFile, setOriginalFile] = useState<File | null>(null); // For name keeping
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);

  const canChooseFromPhotos = profileKind === 'author';

  const title = kind === 'cover' ? 'Edit cover' : 'Edit profile photo';

  const closeAll = () => {
    setMenuOpen(false);
    setLibraryOpen(false);
    setLibraryTab('photos');
    setAlbum(null);
    setSelectedUrl('');
    setLibraryError(null);
    setCropImageSrc(null);
    setOriginalFile(null);
  };

  const escCloseEnabled = useMemo(() => menuOpen || libraryOpen, [libraryOpen, menuOpen]);

  useEffect(() => {
    if (!escCloseEnabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeAll();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [escCloseEnabled]);

  const saveUrl = async (url: string) => {
    const nextUrl = String(url || '').trim();
    if (!nextUrl) return;
    setSaving(true);
    try {
      if (profileKind === 'author') {
        const res = await setPublicAuthorProfileImageUrlAction({
          kind: kind === 'cover' ? 'cover' : 'avatar',
          url: nextUrl,
        });
        if (!res?.success) {
          setLibraryError(res?.error || 'Update failed.');
          return;
        }
      } else {
        const res = await setUserProfileImageUrlAction({
          kind: kind === 'cover' ? 'cover' : 'avatar',
          url: nextUrl,
        });
        if (!res?.success) {
          setLibraryError(res?.error || 'Update failed.');
          return;
        }
      }
      closeAll();
      router.refresh();
    } finally {
      setSaving(false);
    }
  };

  const ensureLibraryLoaded = async (force = false) => {
    if (!canChooseFromPhotos) return;
    if (!force && (libraryItems.length > 0 || libraryLoading)) return;
    setLibraryLoading(true);
    setLibraryError(null);
    try {
      const res = await fetch('/api/public-authors/images', { method: 'GET' });
      const data = await res.json();
      const files = Array.isArray(data?.files) ? data.files : [];
      const mapped = files
        .map((f: any) => ({
          url: String(f?.url || ''),
          name: String(f?.name || f?.filename || ''),
        }))
        .filter((f: LibraryItem) => !!f.url);
      setLibraryItems(mapped);
    } catch {
      setLibraryError('Failed to load your photos.');
    } finally {
      setLibraryLoading(false);
    }
  };

  const openLibrary = async () => {
    setMenuOpen(false);
    setLibraryOpen(true);
    setLibraryTab('photos');
    setAlbum(null);
    setSelectedUrl('');
    await ensureLibraryLoaded();
  };

  const onFileChosenForCrop = (file: File | null) => {
    if (!file) return;
    setOriginalFile(file);
    const objectUrl = URL.createObjectURL(file);
    setCropImageSrc(objectUrl);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const handleLibraryItemUse = (url: string) => {
    if (!url) return;
    setOriginalFile(null);
    setCropImageSrc(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const onCropComplete = useCallback((croppedArea: any, croppedPixels: any) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleSaveCrop = async () => {
    if (!cropImageSrc || !croppedAreaPixels) return;

    setUploading(true);
    setLibraryError(null);
    try {
      const croppedBlob = await getCroppedImg(cropImageSrc, croppedAreaPixels, 0);
      if (!croppedBlob) throw new Error('Could not crop image');

      const fileName = originalFile 
        ? originalFile.name.replace(/\.[^/.]+$/, "") + "_cropped.webp"
        : `cropped_${Date.now()}.webp`;

      const finalFile = new File([croppedBlob], fileName, { type: 'image/webp' });

      const form = new FormData();
      form.append('file', finalFile);
      form.append('folder', kind === 'cover' ? 'cover' : 'profile');
      
      const res = await fetch('/api/upload', { method: 'POST', body: form });
      const data = await res.json();
      if (!data?.success || !data?.url) {
        setLibraryError(data?.message || 'Upload failed.');
        setUploading(false);
        return;
      }
      
      // Clear up Object URL to prevent memory leaks
      if (cropImageSrc.startsWith('blob:')) {
        URL.revokeObjectURL(cropImageSrc);
      }
      setCropImageSrc(null);
      setOriginalFile(null);
      
      await saveUrl(String(data.url));
    } catch (e: any) {
      setLibraryError(e?.message || 'Failed to crop and upload.');
      setUploading(false);
    }
  };

  const gridHeading =
    libraryTab === 'albums' && album
      ? album === 'cover'
        ? 'Cover album'
        : 'Profile album'
      : 'Your photos';

  const coverPreviewUrl =
    typeof currentCoverUrl === 'string' && currentCoverUrl.trim() ? currentCoverUrl.trim() : null;
  const avatarPreviewUrl =
    typeof currentAvatarUrl === 'string' && currentAvatarUrl.trim() ? currentAvatarUrl.trim() : null;

  return (
    <>
      <button
        type="button"
        className={buttonClassName}
        aria-label={title}
        title={title}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setMenuOpen(true);
          setLibraryError(null);
        }}
      >
        <Pencil size={iconSize} />
      </button>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0] || null;
          e.target.value = '';
          onFileChosenForCrop(file);
        }}
      />

      {menuOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeAll();
          }}
        >
          <div className="w-full max-w-sm rounded-2xl border border-[var(--color-border)] bg-[var(--bg-elevated)] shadow-2xl overflow-hidden">
            <div className="p-4 border-b border-[var(--color-border)]">
              <div className="text-sm font-semibold text-[var(--color-text-main)]">{title}</div>
              <div className="text-xs text-[var(--color-text-muted)]">Choose how you want to update it</div>
            </div>
            <div className="p-4 space-y-3">
              <button
                type="button"
                className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--bg-base)] px-4 py-3 text-sm font-semibold text-[var(--color-text-main)] hover:bg-[var(--bg-secondary)] transition-colors disabled:opacity-60"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || saving}
              >
                {uploading ? 'Uploading…' : 'Upload'}
              </button>
              {canChooseFromPhotos && (
                <button
                  type="button"
                  className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--bg-base)] px-4 py-3 text-sm font-semibold text-[var(--color-text-main)] hover:bg-[var(--bg-secondary)] transition-colors disabled:opacity-60"
                  onClick={() => void openLibrary()}
                  disabled={uploading || saving}
                >
                  Choose from Photos
                </button>
              )}
              <button
                type="button"
                className="w-full rounded-xl border border-[var(--color-border)] px-4 py-3 text-sm font-semibold text-[var(--color-text-muted)] hover:bg-[var(--bg-secondary)] transition-colors"
                onClick={closeAll}
                disabled={uploading || saving}
              >
                Cancel
              </button>
              {libraryError && <div className="text-xs text-red-400">{libraryError}</div>}
            </div>
          </div>
        </div>
      )}

      {libraryOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeAll();
          }}
        >
          <div className="w-full max-w-4xl rounded-2xl border border-[var(--color-border)] bg-[var(--bg-elevated)] shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
            <div className="p-4 border-b border-[var(--color-border)] flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-[var(--color-text-main)]">{title}</div>
                <div className="text-xs text-[var(--color-text-muted)]">Choose a photo from your library</div>
              </div>
              <button
                type="button"
                className="text-xs px-3 py-1.5 rounded-lg border border-[var(--border-subtle)] text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] transition-colors"
                onClick={closeAll}
                disabled={uploading || saving}
              >
                Close
              </button>
            </div>

            <div className="px-4 pt-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                    libraryTab === 'photos'
                      ? 'border-[var(--color-primary)] text-[var(--color-primary)]'
                      : 'border-[var(--color-border)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-secondary)]'
                  }`}
                  onClick={() => {
                    setLibraryTab('photos');
                    setAlbum(null);
                  }}
                >
                  Photos
                </button>
                <button
                  type="button"
                  className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                    libraryTab === 'albums'
                      ? 'border-[var(--color-primary)] text-[var(--color-primary)]'
                      : 'border-[var(--color-border)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-secondary)]'
                  }`}
                  onClick={() => {
                    setLibraryTab('albums');
                    setAlbum(null);
                  }}
                >
                  Albums
                </button>
              </div>

              <button
                type="button"
                className="text-xs px-3 py-1.5 rounded-lg border border-[var(--color-border)] text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] transition-colors disabled:opacity-60"
                onClick={() => {
                  void ensureLibraryLoaded(true);
                }}
                disabled={libraryLoading}
                title="Refresh"
              >
                {libraryLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>

            <div className="p-4 flex-1 overflow-auto custom-scrollbar">
              {libraryTab === 'albums' && album === null ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button
                    type="button"
                    className="rounded-2xl border border-[var(--color-border)] bg-[var(--bg-base)] p-5 text-left hover:bg-[var(--bg-secondary)] transition-colors"
                    onClick={() => setAlbum('cover')}
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-14 w-14 rounded-xl border border-[var(--color-border)] bg-[var(--bg-secondary)] overflow-hidden flex items-center justify-center">
                        {coverPreviewUrl ? (
                          <img
                            src={coverPreviewUrl}
                            alt="Cover"
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="h-full w-full bg-[var(--bg-secondary)]" />
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-[var(--text-primary)]">Cover</div>
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    className="rounded-2xl border border-[var(--color-border)] bg-[var(--bg-base)] p-5 text-left hover:bg-[var(--bg-secondary)] transition-colors"
                    onClick={() => setAlbum('profile')}
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-14 w-14 rounded-xl border border-[var(--color-border)] bg-[var(--bg-secondary)] overflow-hidden flex items-center justify-center">
                        {avatarPreviewUrl ? (
                          <img
                            src={avatarPreviewUrl}
                            alt="Profile"
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="h-full w-full bg-[var(--bg-secondary)]" />
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-[var(--text-primary)]">Profile</div>
                      </div>
                    </div>
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-semibold text-[var(--text-primary)]">{gridHeading}</div>
                    {libraryTab === 'albums' && album ? (
                      <button
                        type="button"
                        className="text-xs px-3 py-1.5 rounded-lg border border-[var(--color-border)] text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] transition-colors"
                        onClick={() => setAlbum(null)}
                      >
                        Back to Albums
                      </button>
                    ) : null}
                  </div>

                  {libraryLoading ? (
                    <div className="text-sm text-[var(--color-text-muted)]">Loading…</div>
                  ) : libraryItems.length === 0 ? (
                    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--bg-base)] p-6 text-sm text-[var(--color-text-muted)]">
                      No photos yet. Use Upload first.
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      {libraryItems.map((item) => {
                        const active = selectedUrl === item.url;
                        return (
                          <button
                            key={item.url}
                            type="button"
                            className={`group rounded-xl border overflow-hidden bg-[var(--bg-base)] transition-colors ${
                              active
                                ? 'border-[var(--color-primary)]'
                                : 'border-[var(--color-border)] hover:border-[var(--color-primary)]/50'
                            }`}
                            onClick={() => setSelectedUrl(item.url)}
                            title={item.name}
                          >
                            <img
                              src={item.url}
                              alt={item.name || 'photo'}
                              className="aspect-square w-full object-cover"
                              loading="lazy"
                            />
                          </button>
                        );
                      })}
                    </div>
                  )}

                  <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                    <div className="text-xs text-[var(--color-text-muted)]">
                      {selectedUrl ? 'Selected' : 'Select a photo to continue'}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="text-xs px-3 py-2 rounded-lg border border-[var(--color-border)] text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] transition-colors disabled:opacity-60"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading || saving}
                      >
                        Upload New
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary text-xs px-4 py-2 disabled:opacity-60"
                        onClick={() => handleLibraryItemUse(selectedUrl)}
                        disabled={!selectedUrl || uploading || saving}
                      >
                        Use & Crop
                      </button>
                    </div>
                  </div>

                  {libraryError && <div className="text-xs text-red-400">{libraryError}</div>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cropper Modal */}
      {cropImageSrc && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/80 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-2xl bg-[var(--bg-elevated)] border border-[var(--color-border)] rounded-2xl shadow-2xl overflow-hidden flex flex-col h-[80vh]">
            <div className="p-4 border-b border-[var(--color-border)] flex items-center justify-between gap-3 shrink-0">
              <div>
                <div className="text-sm font-semibold text-[var(--color-text-main)]">Adjust {title.replace('Edit ', '')}</div>
                <div className="text-xs text-[var(--color-text-muted)]">Drag to pan, slider to zoom</div>
              </div>
              <button
                type="button"
                className="text-xs px-3 py-1.5 rounded-lg border border-[var(--border-subtle)] text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] transition-colors"
                onClick={() => setCropImageSrc(null)}
                disabled={uploading || saving}
              >
                Cancel
              </button>
            </div>

            <div className="relative flex-grow bg-black w-full overflow-hidden">
              <Cropper
                image={cropImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={kind === 'cover' ? 16 / 5 : 1}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            </div>

            <div className="p-4 bg-[var(--bg-elevated)] border-t border-[var(--color-border)] shrink-0 flex flex-col md:flex-row items-center gap-4 justify-between">
              <div className="flex items-center gap-3 w-full md:w-auto">
                <ZoomOut size={16} className="text-[var(--text-muted)] shrink-0" />
                <input
                  type="range"
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.1}
                  aria-labelledby="Zoom"
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="w-full md:w-32 h-1 bg-[var(--border-subtle)] rounded-lg appearance-none cursor-pointer accent-[var(--color-primary)]"
                />
                <ZoomIn size={16} className="text-[var(--text-muted)] shrink-0" />
              </div>
              <div className="flex w-full md:w-auto items-center gap-3">
                 <button
                  type="button"
                  className="w-full md:w-auto rounded-xl border border-[var(--color-border)] px-4 py-2 text-sm font-semibold text-[var(--color-text-muted)] hover:bg-[var(--bg-secondary)] transition-colors"
                  onClick={() => setCropImageSrc(null)}
                  disabled={uploading || saving}
                >
                  Back
                 </button>
                 <button
                  type="button"
                  className="w-full md:w-auto btn btn-primary flex-1 md:flex-none uppercase font-bold tracking-widest text-xs px-6 py-2 transition-all disabled:opacity-50"
                  onClick={handleSaveCrop}
                  disabled={uploading || saving || !croppedAreaPixels}
                 >
                  {uploading || saving ? 'Saving...' : 'Confirm'}
                 </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
